import { useRef, useEffect, useState, useContext } from 'react';
import { useTranslation } from '@/utils/i18n/useTranslation';

import { ModalContext } from '@/context/ModalContext';
import { UserContext } from '@/context/UserContext';
import convertDataToTranslation from '@/utils/data/convertDataToTranslation';

import Svg from '@/components/Svg/Svg';
import InfoBox from '@/components/InfoBox/InfoBox';
import RichTextBlock from '@/components/RichTextBlock/RichTextBlock';
import PlayerList from '@/components/PlayerList/PlayerList';
import type {
  FormItemComponentProps,
  FormRichText,
  Player,
  TranslateFn,
} from '@/types/domain';

const revealClass = ' is-revealed';
const activeClass = ' is-active';

const createMarkup = (copy?: string) =>
  ({ __html: copy }) as { __html: string };

const FormItemQuestion = (props: FormItemComponentProps) => {
  const { item, groupedState, revealState } = props;

  const modalContext = useContext(ModalContext);
  const userContext = useContext(UserContext);
  /* eslint-disable @typescript-eslint/no-unnecessary-condition -- optimistic GraphQL type — this guard defends a null the generated type doesn't model; re-type from codegen to remove */
  const { t } = useTranslation(
    userContext?.userSession?.sportType?.slug ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_NAMESPACE ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_SPORT,
  );
  /* eslint-enable @typescript-eslint/no-unnecessary-condition */

  const [isGrouped, setIsGrouped] = useState(false);
  const itemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- optimistic GraphQL type — this guard defends a null the generated type doesn't model; re-type from codegen to remove
    if (!(item?.group && groupedState)) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- optimistic GraphQL type — this guard defends a null the generated type doesn't model; re-type from codegen to remove
    const itemLabel = item?.group?.label;
    const groupedItem = groupedState.find((str) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
      return str.label.includes(itemLabel!);
    });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, react-hooks/set-state-in-effect -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
    setIsGrouped(groupedItem!.active);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- RHF form-item lifecycle effect; intentional trigger (adding the recreated onFormItem* callbacks / prop values would loop)
  }, [groupedState]);

  // TO REFACTOR
  useEffect(() => {
    const element = document.querySelector('.InfoBox-wrapper');
    if (element) element.classList.add('InfoBox-first');
  }, []);

  const handleRedirectClick = (e: Event) => {
    e.preventDefault();
    modalContext.setModalPromptRedirect((e.target as HTMLAnchorElement).href);
    modalContext.setModalPromptTrigger(true);
  };

  useEffect(() => {
    const redirectEl = document.querySelector('.FormItem-questionRedirect');
    redirectEl?.addEventListener('click', handleRedirectClick);
    return () => redirectEl?.removeEventListener('click', handleRedirectClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- RHF form-item lifecycle effect; intentional trigger (adding the recreated onFormItem* callbacks / prop values would loop)
  }, [modalContext.modalIsDirty]);

  const getDynamicRichTextTranslation = () => {
    return item.richTextDynamic
      ? t(item.richText as string)
      : t(item.richText as string);
  };

  /* eslint-disable @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove */
  return (
    <div
      ref={itemRef}
      className={`FormItem ${item.infoBox ? 'FormItem--infoBox' : ''} FormItem--${String(item.type)}${
        item.modifier ? ` FormItem--${item.modifier}` : ''
      }${revealState ? revealClass : ''}${isGrouped ? activeClass : ''}`}
      data-reveal-target={item.revealed}
    >
      {item.heading && (
        <h3
          className="FormItem-questionHeading"
          dangerouslySetInnerHTML={createMarkup(
            item.transl ? t(item.heading) : item.heading,
          )}
        ></h3>
      )}
      {item.subHeading && (
        <h4
          className="FormItem-questionSubHeading"
          dangerouslySetInnerHTML={createMarkup(
            item.transl ? t(item.subHeading) : item.subHeading,
          )}
        ></h4>
      )}
      {item.label && (
        <p
          className="FormItem-questionCopy"
          dangerouslySetInnerHTML={createMarkup(
            item.transl ? t(item.label) : item.label,
          )}
        ></p>
      )}
      {(item.richText as boolean) && (
        <RichTextBlock
          content={
            !(item.richText as FormRichText).data && item.richText
              ? item.transl
                ? getDynamicRichTextTranslation()
                : item.richText
              : ''
          }
          data={
            (item.richText as FormRichText).data
              ? (item.richText as FormRichText).data &&
                (item.richText as FormRichText).transl
                ? convertDataToTranslation(
                    (item.richText as FormRichText).data as Parameters<
                      typeof convertDataToTranslation
                    >[0],
                    t as TranslateFn,
                  )
                : (item.richText as FormRichText).data
              : []
          }
          modifiers={['RichTextBlock--modalContent']}
        />
      )}
      {item.note && (
        <p
          className="FormItem-questionNote"
          dangerouslySetInnerHTML={createMarkup(
            item.transl ? t(item.note) : item.note,
          )}
        ></p>
      )}
      {item.alert && (
        <p className="FormItem-questionAlert">
          <span className="FormItem-questionAlertIndicator">!</span>
          <span
            className="FormItem-questionAlertCopy"
            dangerouslySetInnerHTML={createMarkup(item.alert)}
          ></span>
        </p>
      )}
      {(item.infoBox as boolean) && (
        <InfoBox
          content={[item.infoBox] as { tooltipText?: string }[]}
          modifiers={[
            `FormItem--infoBox ${item.infoBoxBottom ? 'FormItem--infoBoxBottom' : ''}`,
          ]}
        />
      )}
      {item.specialHeading && (
        <div className="FormItem-questionNotice">
          <hr className="FormItem-questionNoticeRuler" />
          <div className="FormItem-questionNoticeBlock">
            <Svg
              modifiers={['FormItem-questionNoticeIcon']}
              id={item.icon as string}
            />
            <div>
              <h3
                className="FormItem-questionNoticeTitle"
                dangerouslySetInnerHTML={createMarkup(
                  t(item.specialHeading).toUpperCase(),
                )}
              ></h3>
              <p
                className="FormItem-questionNoticeDescription"
                dangerouslySetInnerHTML={createMarkup(
                  t(item.specialSubheading!),
                )}
              ></p>
            </div>
          </div>
        </div>
      )}
      {item.playerlist && (
        <>
          {item.playerlist.title && (
            <h3
              className="FormItem-questionSubHeading"
              dangerouslySetInnerHTML={createMarkup(
                item.transl ? t(item.playerlist.title) : item.playerlist.title,
              )}
            ></h3>
          )}
          {item.playerlist.description && (
            <p
              className="FormItem-questionNote"
              dangerouslySetInnerHTML={createMarkup(
                item.transl
                  ? t(item.playerlist.description)
                  : item.playerlist.description,
              )}
            ></p>
          )}
          {(item.playerlist.list as boolean) && (
            <PlayerList playerList={[item.playerlist.list] as Player[][]} />
          )}
        </>
      )}
    </div>
  );
  /* eslint-enable @typescript-eslint/no-non-null-assertion */
};

export default FormItemQuestion;
